import { inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { Integration } from "src/app/backend/models/integration";
import { PaginatedResult } from "src/app/support/paginated-result";
import { environment } from "src/environments/environment";
import { map } from "rxjs/operators";
import { Installation } from "src/app/backend/models/installation";
import { dateUtil } from "src/app/support/date-util";
import { Regions } from "src/app/services/feature-access-control/region.service";

@Injectable()
export class IntegrationBackend {
  private readonly http = inject(HttpClient);

  load(id: string): Observable<Integration> {
    return this.http
      .get<Integration>(
        `${environment.snoots.api.integrator}/integrations/${id}`
      )
      .pipe(map(extend));
  }

  listAll(): Observable<Integration[]> {
    return this.http
      .get<PaginatedResult<Integration>>(
        `${environment.snoots.api.integrator}/integrations`
      )
      .pipe(
        map((response) =>
          response.docs
            .filter((integration) => integration.id !== "provet")
            .map(extend)
        )
      );
  }

  loadAuthorizationUrl(id: string): Observable<string> {
    return this.http
      .post<{
        url: string;
      }>(`${environment.snoots.api.integrator}/oauth/authorization-urls`, {
        integrationId: id
      })
      .pipe(map((response) => response.url));
  }

  install(id: string): Observable<Installation> {
    return this.http.post<Installation>(
      `${environment.snoots.api.integrator}/integrations/${id}/installations`,
      {}
    );
  }
}
/* TODO: rimuovere commenti proprietà ormai censite su db */
function extend(integration: Integration): Integration {
  if (integration.id === "vetinfo") {
    integration.id = "vetinfo-scorta";
  }

  integration.developer = "Snoots SRL";

  if (integration?.pricing?.type === "subscription") {
    integration.price = integration?.pricing?.price || 0;
    integration.pricePeriod = "month";
  } else {
    integration.price = "free";
  }

  integration.new = false;

  switch (integration.id) {
    case "provet": {
      /*integration.regions = [
        Regions.IT,
        Regions.EN,
        Regions.ES,
        Regions.FR,
        Regions.AE,
        Regions.CH,
        Regions.KA
      ];*/
      integration.categories = ["provet", "veterinary"];
      integration.icon = "/assets/images/integrations/provet/provet.png";
      integration.descriptionLong =
        "Provet is a veterinary software company that provides a range of products and services for the veterinary industry.";
      break;
    }
    case "snoots-mcp": {
      integration.name = "Snoots Connector for AI";
      integration.description =
        "Integra Snoots con i tuoi assistenti AI preferiti. Interroga clienti, pazienti, analytics e molto altro direttamente in linguaggio naturale.";
      integration.icon = "/assets/images/integrations/mcp/mcp.png";
      integration.images = ["/assets/images/integrations/mcp/image1.png"];
      integration.categories = ["mcp", "ai"];
      integration.publishedOn = dateUtil(
        "17/06/2026",
        "dd/MM/YYYY"
      ).toISOString();
      integration.updatedOn = dateUtil(
        "17/06/2026",
        "dd/MM/YYYY"
      ).toISOString();
      integration.new = true;
      break;
    }
    case "vetinfo-scorta": {
      /*integration.regions = [Regions.IT];*/
      integration.categories = ["wharehouse", "database"];
      integration.publishedOn = dateUtil(
        "21/02/2025",
        "dd/MM/YYYY"
      ).toISOString();
      integration.updatedOn = dateUtil(
        "05/03/2025",
        "dd/MM/YYYY"
      ).toISOString();

      integration.descriptionLong =
        "L’integrazione con VetInfo in Snoots PMS consente l’accesso diretto ai dati anagrafici e sanitari degli animali, la verifica dello stato delle vaccinazioni, la gestione delle proprietà e la registrazione di nuovi microchip, tutto in conformità con le normative vigenti. Automatizzando l’aggiornamento e la sincronizzazione delle informazioni, questa integrazione riduce gli errori di trascrizione, velocizza il flusso di lavoro e migliora l’efficienza operativa delle cliniche veterinarie, offrendo un’esperienza più fluida sia per i professionisti che per i proprietari degli animali.";
      break;
    }
    case "idexx-vcplus": {
      /*integration.regions = [
        Regions.IT,
        Regions.AE,
        Regions.FR,
        Regions.EN,
        Regions.ES
      ];*/
      integration.categories = [
        "laboratory",
        "diagnostic",
        "clinic-management"
      ];
      integration.publishedOn = dateUtil(
        "16/03/2025",
        "dd/MM/YYYY"
      ).toISOString();
      integration.updatedOn = dateUtil(
        "21/03/2025",
        "dd/MM/YYYY"
      ).toISOString();

      integration.descriptionLong =
        "L’integrazione con IDEXX VetConnect PLUS (VCPlus) in Snoots PMS consente un accesso immediato ai risultati di laboratorio, automatizzando il flusso di lavoro diagnostico. Grazie alla sincronizzazione in tempo reale, i referti degli esami vengono ricevuti e archiviati direttamente nella scheda del paziente, riducendo gli errori manuali e velocizzando le decisioni cliniche. Questa integrazione migliora l’efficienza operativa della clinica, garantendo un monitoraggio più accurato della salute degli animali e offrendo ai veterinari strumenti avanzati per una diagnosi tempestiva e precisa.";
      break;
    }
    case "vetinfo-rev": {
      /*integration.regions = [Regions.IT];*/
      integration.categories = ["prescriptions"];
      integration.publishedOn = dateUtil(
        "02/04/2025",
        "dd/MM/YYYY"
      ).toISOString();
      integration.updatedOn = dateUtil(
        "02/04/2025",
        "dd/MM/YYYY"
      ).toISOString();

      integration.descriptionLong =
        "L’integrazione con VetInfo REV in Snoots PMS semplifica la gestione e la trasmissione delle ricette elettroniche veterinarie, garantendo conformità normativa e riducendo gli errori di compilazione. Grazie alla sincronizzazione diretta con il sistema ufficiale, i veterinari possono emettere, consultare e archiviare le prescrizioni in modo rapido e sicuro, migliorando l’efficienza operativa e offrendo un servizio più fluido ai proprietari degli animali.";
      break;
    }
    case "brave-contabile": {
      integration.name = "Brave Contabile";
      integration.icon =
        "/assets/images/integrations/brave-contabile/brave-contabile.png";
      integration.images = [
        "/assets/images/integrations/brave-contabile/image1.png"
      ];
      /*integration.regions = [Regions.IT];*/
      integration.categories = ["accounting"];
      integration.publishedOn = dateUtil(
        "26/02/2026",
        "dd/MM/YYYY"
      ).toISOString();
      integration.updatedOn = dateUtil(
        "26/02/2026",
        "dd/MM/YYYY"
      ).toISOString();
      integration.descriptionLong =
        "L’integrazione con Brave Contabile in Snoots PMS consente la sincronizzazione dei dati contabili e amministrativi, migliorando l’efficienza operativa della clinica veterinaria.";
      integration.developer = "Brave Vet";
      integration.new = true;
      break;
    }
    case "is-abv": {
      integration.name = "IS ABV";
      integration.description =
        "Sistema d'informazione sugli antibiotici in medicina veterinaria";
      integration.icon = "/assets/images/integrations/is-abv/logo.png";
      integration.images = [
        "/assets/images/integrations/is-abv/logo_de.png",
        "/assets/images/integrations/is-abv/logo_it.png"
      ];
      /*integration.regions = [Regions.CH];*/
      integration.categories = ["prescriptions"];
      integration.publishedOn = dateUtil(
        "20/03/2026",
        "dd/MM/YYYY"
      ).toISOString();
      integration.updatedOn = dateUtil(
        "20/03/2026",
        "dd/MM/YYYY"
      ).toISOString();
    }
  }

  return integration;
}
